import React, { createContext, useState, useEffect } from 'react';
import apiClient, { refreshAccessToken as exchangeRefreshCookie } from '../apiClient';
import { setAccessToken, clearAccessToken } from '../authToken';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const checkIsAllowedPath = (currentPath) => {
        const allowed = user?.shareLinkData?.allowedPath;
        if (!allowed) return false;
        return currentPath === allowed || currentPath.startsWith(`${allowed}/`);
    };

  /**
   * Logs the user in by storing the token and setting the user state.
   * Ensures user_id, owner_id, and role_id are set and correctly typed.
   */
const login = (token, userData, shareLinkData = null) => {
    // MGMT-0042: in memory only. The durable half of the session is the
    // httpOnly refresh cookie the server set on /api/auth/login.
    setAccessToken(token);
    
    // If we have shareLinkData, store the UUID token separately for the API middleware
    if (shareLinkData?.shareToken) {
        localStorage.setItem('share_token_uuid', shareLinkData.shareToken);
    } else {
        localStorage.removeItem('share_token_uuid');
    }

    setUser({
        ...userData,
        user_id: Number(userData.user_id),
        owner_id: userData.owner_id ? Number(userData.owner_id) : null,
        role_id: Number(userData.role_id),
        must_change_password: !!userData.must_change_password,
        // 💡 ADD THESE TWO LINES:
        shareLinkData: shareLinkData, 
        isShareLinkUser: !!shareLinkData
    });
    setError(null);
};

  const doLogout = () => {
    // Drop the in-memory token first so nothing in flight can reuse it, then
    // clear the refresh + CSRF cookies server-side. The call carries the CSRF
    // header via the apiClient request interceptor (MGMT-0042).
    clearAccessToken();
    localStorage.removeItem('share_token_uuid');
    apiClient.post('/api/auth/logout', {}).catch(() => { /* best effort */ });
    setUser(null);
    setError(null);
  };

  const logoutWithLoading = () => {
    doLogout();
    setLoading(false);
  };

  /**
   * Attempts to refresh the access token using the refresh token cookie.
   * If successful, fetches new user data and updates the state.
   */
  const refreshAccessToken = async () => {
    try {
      const { data } = await apiClient.post('/api/auth/refresh', {}, { withCredentials: true });
      setAccessToken(data.token);

      // The /api/auth/refresh response already contains the updated 'user' object (data.user)
      // If the server returns the full user object on refresh, we can use it directly.
      // Assuming it does:
      const userData = data.user; 
      setUser({
        ...userData,
        user_id: Number(userData.user_id),
        owner_id: userData.owner_id ? Number(userData.owner_id) : null,
        role_id: Number(userData.role_id),
        // Removed: permissionRank
      });
      setError(null);
      return data.token;
    } catch (err) {
      console.error('Token refresh failed:', err.message);
      setError(err.message);
      logoutWithLoading();
      return null;
    }
  };

  /**
   * Initialization effect: checks for existing token and fetches user data.
   */
    useEffect(() => {
        const initAuth = async () => {
            // MGMT-0042: nothing survives a reload except the httpOnly refresh
            // cookie, so a cold start always begins by exchanging it. A failure
            // here just means "not signed in" — public pages (/blackbook, share
            // links) must keep rendering, so it never redirects.
            let token = null;
            try {
                token = await exchangeRefreshCookie({ bootstrap: true });
            } catch (e) {
                clearAccessToken();
                setLoading(false);
                return;
            }
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // Try fetching user with current token
                const response = await apiClient.get('/api/user', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const userData = response.data;
                setUser({
                    ...userData,
                    user_id: Number(userData.user_id),
                    owner_id: userData.owner_id ? Number(userData.owner_id) : null,
                    role_id: Number(userData.role_id),
                });
                setError(null);
            } catch (err) {
                console.warn('Initial user fetch failed:', err.response?.status);

                if (err.response?.status === 401) {
                    // Try refreshing token
                    const newToken = await refreshAccessToken();
                    if (newToken) {
                        try {
                            const retryResp = await apiClient.get('/api/user', {
                                headers: { Authorization: `Bearer ${newToken}` }
                            });
                            const userData = retryResp.data;
                            setUser({
                                ...userData,
                                user_id: Number(userData.user_id),
                                owner_id: userData.owner_id ? Number(userData.owner_id) : null,
                                role_id: Number(userData.role_id),
                            });
                            setError(null);
                        } catch (retryErr) {
                            console.error('Retry after refresh failed:', retryErr.message);
                            logoutWithLoading();
                        }
                    } else {
                        // Refresh failed, log out
                        logoutWithLoading();
                    }
                } else {
                    logoutWithLoading();
                }
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            logout: doLogout, 
            loading, 
            error, 
            refreshAccessToken,
            isShareLinkUser: !!user?.isShareLinkUser,
            checkIsAllowedPath
        }}>
          {children}
        </AuthContext.Provider>
    ); 
};